import { Trans, useLingui } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import {
  HealthProReportType,
  PatientHealthProReportsResponse,
} from "../../../api";
import { AppModel } from "../../../app/app-model";
import { NativeSelect } from "../../../components/ui-kit/base/native-select";
import { Button } from "../../../components/ui-kit/button";
import { DateInput } from "../../../components/ui-kit/fields/date-input";
import { NumberInput } from "../../../components/ui-kit/fields/number-input";
import { RichTextContent } from "../../../components/ui-kit/rich-text-content";
import { healthProPhysicians } from "../../../constants/enum-to-text";
import { dateTimeOrNull } from "../../../utils/utils";
import { PatientSubpage } from "../common/patient-subpage";
import { HealthProDetails } from "./health-pro-details";
import styles from "./health-pro.module.css";

export const HealthPro = observer(function HealthPro(props: { app: AppModel }) {
  const { t } = useLingui();
  const patient = props.app.requirePatient();
  const [reportType, setReportType] = useState<HealthProReportType>(
    HealthProReportType.basic,
  );
  const [maxReports, setMaxReports] = useState(10);
  const [fromDate, setFromDate] = useState<string | null>(null);
  const [reports, setReports] =
    useState<PatientHealthProReportsResponse | null>(null);

  return (
    <PatientSubpage title={t`HealthPro reports`}>
      <HealthProDetails app={props.app} />
      <br className="h-10" />
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1">
          <Trans>Report type:</Trans>
          <NativeSelect
            className="w-auto"
            value={reportType}
            onChange={(e) =>
              setReportType(e.currentTarget.value as HealthProReportType)
            }
          >
            <option value={HealthProReportType.basic}>{t`Basic`}</option>
            <option value={HealthProReportType.dovera}>Dôvera</option>
            <option value={HealthProReportType.vszp_basic}>
              {t`VšZP weekly`}
            </option>
            <option value={HealthProReportType.vszp_monthly}>
              {t`VšZP monthly`}
            </option>
          </NativeSelect>
        </label>
        <label className="flex items-center gap-1">
          <Trans>Maximum number of reports:</Trans>
          <NumberInput
            width={60}
            value={maxReports}
            onChange={(newValue) => {
              setMaxReports(newValue ?? 0);
            }}
          />
        </label>
        <label className="flex items-center gap-1">
          <Trans>From date:</Trans>
          <DateInput
            type="date"
            value={fromDate ?? ""}
            onChange={(newValue) => {
              setFromDate(newValue);
            }}
          />
        </label>
        <Button
          variant="primary"
          onClick={async () => {
            const reports = await patient.generateHealthProReports(
              reportType,
              maxReports,
              fromDate,
            );
            setReports(reports);
          }}
        >
          <Trans>Generate reports</Trans>
        </Button>
      </div>
      {reports != null && (
        <div>
          <h4 className="mt-5 mb-5 font-bold">
            <Trans>Reports:</Trans>
          </h4>
          <div>
            <Trans>
              Found <strong>{reports.num_reports}</strong> reports.
            </Trans>
            <br className="h-5" />
            <Trans>
              (Asked for {reports.max_reports} reports from {reports.from_date})
            </Trans>
          </div>
          <div>
            <table className={styles.reportsTable}>
              <tbody>
                {reports.reports.map((report, reportIndex) => (
                  <tr key={reportIndex}>
                    <td>
                      <strong>{reportIndex + 1}. </strong>
                      <Trans>report:</Trans>
                    </td>
                    <td>
                      {dateTimeOrNull(report.report_datetime, true)}
                      <br className="h-10" />
                      <Trans>Dg:</Trans>{" "}
                      <strong>{report.report_main_diagnosis}</strong>
                      <br className="h-5" />
                      <Trans>Service:</Trans>{" "}
                      <strong>{report.report_service}</strong>
                      <br className="h-5" />
                      <strong>
                        {healthProPhysicians[report.report_physician]}
                      </strong>
                    </td>
                    <td>
                      <RichTextContent value={report.report_text} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </PatientSubpage>
  );
});
